import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { FaCheckCircle, FaBolt, FaTint, FaCog, FaArrowRight, FaShieldAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Cta from '../../components/NewCallToAction';

const BRAND_BLUE = "#3b82f6";
const DARK_SLATE = "#0f172a";

const ServiceDetailPage = ({ title, subtitle, heroBg, description, highlights, coreCompetencies, faqs }) => {
  return (
    <main style={{ background: '#f8fafc', minHeight: '100vh', overflowX: 'hidden' }}>
      <Helmet>
        <title>{title} | Abraj Al Dhafna Qatar</title>
        <meta name="description" content={description.substring(0, 155)} />
        <link rel="canonical" href={`https://abrajaldhafna.com/services/${title.toLowerCase().replace(/ /g, '-')}`} />
      </Helmet>

      {/* Breadcrumb & Back */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '15px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px', display: 'flex', gap: '10px', alignItems: 'center', fontSize: '14px' }}>
          <Link to="/" style={{ color: '#64748b', textDecoration: 'none' }}>Home</Link>
          <span style={{ color: '#cbd5e1' }}>/</span>
          <Link to="/services" style={{ color: '#64748b', textDecoration: 'none' }}>Services</Link>
          <span style={{ color: '#cbd5e1' }}>/</span>
          <span style={{ color: DARK_SLATE, fontWeight: 600 }}>{title}</span>
        </div>
      </div>

      {/* Header / Hero Section */}
      <section style={{ background: heroBg, padding: '100px 20px 80px', color: '#fff', position: 'relative', overflow: 'hidden' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span style={{ background: 'rgba(255,255,255,0.2)', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', marginBottom: '20px', display: 'inline-block' }}>CORE CAPABILITY</span>
            <h1 style={{ fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', fontWeight: 900, marginBottom: '20px', lineHeight: 1.1 }}>{title}</h1>
            <p style={{ fontSize: 'clamp(1.1rem, 2vw, 1.3rem)', opacity: 0.9, maxWidth: '700px', lineHeight: 1.6 }}>{subtitle}</p>
          </motion.div>
        </div>
        <div style={{ position: 'absolute', bottom: '-50px', right: '-50px', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)', borderRadius: '50%' }} />
      </section>

      {/* Main Narrative Section */}
      <section style={{ padding: '80px 20px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '60px' }}>
          <div>
            <h2 style={{ fontSize: '2rem', fontWeight: 800, color: DARK_SLATE, marginBottom: '25px' }}>Excellence in {title} Execution</h2>
            <div style={{ fontSize: '16px', lineHeight: 1.8, color: '#475569', marginBottom: '30px' }}>
              <p style={{ marginBottom: '20px' }}>{description}</p>
              <p>Operating under strict compliance with Qatar General Electricity & Water Corporation (Kahramaa) and civil defense regulations, Abraj Al Dhafna ensures your building systems maintain 100% reliable uptime. Our technicians are fully trained in preventative diagnostics and rapid-incident response.</p>
            </div>

            <h3 style={{ fontSize: '1.4rem', fontWeight: 700, color: DARK_SLATE, marginBottom: '20px' }}>Key Service Highlights</h3>
            <ul style={{ listStyle: 'none', padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
              {highlights.map((highlight, i) => (
                <li key={i} style={{ display: 'flex', gap: '12px', alignItems: 'center', fontSize: '15px', color: '#475569' }}>
                  <FaCheckCircle style={{ color: "#10b981", flexShrink: 0 }} />
                  <span>{highlight}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Side Stats / Summary */}
          <div style={{ background: '#fff', borderRadius: '24px', padding: '40px', boxShadow: '0 10px 30px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0', height: 'fit-content' }}>
            <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: DARK_SLATE, marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f1f5f9' }}>Project Capabilities</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <span style={{ fontSize: '12px', textTransform: 'uppercase', fontWeight: 800, color: '#94a3b8', display: 'block', marginBottom: '5px' }}>Labor Deployment</span>
                <strong style={{ fontSize: '18px', color: DARK_SLATE }}>Certified Electricians & Plumbers</strong>
              </div>
              <div>
                <span style={{ fontSize: '12px', textTransform: 'uppercase', fontWeight: 800, color: '#94a3b8', display: 'block', marginBottom: '5px' }}>Testing Protocol</span>
                <strong style={{ fontSize: '18px', color: DARK_SLATE }}>Post-Install Circuit & Pressure Audits</strong>
              </div>
              <div>
                <span style={{ fontSize: '12px', textTransform: 'uppercase', fontWeight: 800, color: '#94a3b8', display: 'block', marginBottom: '5px' }}>Available Regions</span>
                <strong style={{ fontSize: '18px', color: DARK_SLATE }}>Doha, Lusail, The Pearl, Al Wakrah</strong>
              </div>
            </div>
            <Link to="/contact" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '40px', background: "#10b981", color: '#fff', textDecoration: 'none', padding: '16px', borderRadius: '12px', fontWeight: 700, transition: 'transform 0.2s' }}>
              Request A Quote <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* In-Depth Competencies */}
      <section style={{ background: '#fff', padding: '80px 20px', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: 900, color: DARK_SLATE, textAlign: 'center', marginBottom: '50px' }}>Detailed Competency Portfolio Portfolio</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px' }}>
            {coreCompetencies.map((comp, i) => (
              <div key={i} style={{ background: '#f8fafc', padding: '30px', borderRadius: '20px', border: '1px solid #f1f5f9' }}>
                <div style={{ width: '50px', height: '50px', background: `#10b98115`, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: "#10b981", fontSize: '1.5rem', marginBottom: '20px' }}>{comp.icon}</div>
                <h4 style={{ fontSize: '1.2rem', fontWeight: 800, color: DARK_SLATE, marginBottom: '10px' }}>{comp.title}</h4>
                <p style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.6 }}>{comp.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Cta />
    </main>
  );
};

const MepContracting = () => {
  const pageData = {
    title: "MEP Contracting & Maintenance",
    subtitle: "Specialized Mechanical, Electrical, and Plumbing systems installation and rapid technical manpower supply.",
    heroBg: "linear-gradient(135deg, #065f46 0%, #10b981 100%)",
    description: "Abraj Al Dhafna provides full-scale Mechanical, Electrical, and Plumbing (MEP) subcontracting across Qatar. We deploy pre-vetted, certified HVAC specialists, electrical engineers, and industrial plumbers to deliver comprehensive system setups, diagnostic inspections, and preventative maintenance. From complex distribution boards in commercial towers to critical fluid networks in factory units, we bridge technical engineering with flawless physical execution.",
    highlights: [
      "Power Distribution & High/Low Voltage Systems",
      "Industrial & Commercial Plumbing Networks",
      "Centralized HVAC Ducting & Chiller Setup",
      "Fire Fighting and Suppression Infrastructure",
      "Drainage and Sanitary Containment Systems",
      "Preventative Annual Maintenance Contracts"
    ],
    coreCompetencies: [
      {
        icon: <FaBolt />,
        title: "Electrical Systems & Low Voltage",
        description: "Providing expert wiring, panelboard (DB) terminations, containment routing, and lighting integrations fully compliant with Kahramaa grid mandates."
      },
      {
        icon: <FaCog />,
        title: "Mechanical & HVAC Services",
        description: "Specialized manpower for central air systems, chilled water networks, ductwork fabrication, and precise equipment alignment to optimize performance."
      },
      {
        icon: <FaTint />,
        title: "Plumbing & Fluid Distribution",
        description: "Complete piping installations, booster pump configurations, drainage, and high-pressure testing to ensure leak-free operations."
      }
    ]
  };

  return <ServiceDetailPage {...pageData} />;
};

export default MepContracting;
